<script lang="ts" setup>
import { CartStateEnum, useCartStore } from '@/stores/cart/useCartStore';
import { useTransactionStore } from '@/stores/transaction/useTransactionStore';
import { computed, storeToRefs, toShortAddress, useProviderConfig } from '#imports';
import useBuyNft from '~/composables/web3/useBuyNft.client';

const { transactions } = storeToRefs(useTransactionStore());

const { links: { scanner } } = useProviderConfig()(['links']);

const foundReverted = computed(() => transactions.value.find(item => item.status === 'reverted'));

const { buyNFT, loadingState, AsyncState } = useBuyNft();

const isLoading = computed(() => Object.values(loadingState.value).some(value => value === AsyncState.IS_LOADING));

const cartStore = useCartStore();

const toNext = () => {
    cartStore.setCartState({ state: CartStateEnum.SUCCESS });
    cartStore.clearCart();
};

</script>

<template>
  <div v-for="{ hash, tokenList, status } in transactions"
       :key="hash"
       class="display-flex flex-column gap-16"
  >
    <div class="display-flex align-items-center justify-between">
      <a v-if="tokenList[0]?.network"
         :href="`${scanner[tokenList[0]?.network].tx}${hash}`"
         class="text-600-accent display-flex align-items-center"
         target="_blank"
      >
        {{ toShortAddress(hash) }}
        <IconMain class="ml-8"
                  :icon="scanner[tokenList[0]?.network].icon"
                  :size="24"
        />
      </a>
      <LoaderDots v-if="status === 'pending'" />
      <p v-else-if="status === 'success' || status === 'reverted'"
         :class="`transaction__status--${status}`"
      >
        {{ status }}
      </p>
    </div>
    <TransitionGroup name="list"
                     tag="div"
                     class="display-flex flex-column gap-8"
    >
      <div v-for="({ displayImageURL, name }, index) in tokenList"
           :key="index"
           class="display-flex justify-between align-items-center gap-16"
      >
        <div class="transaction-row__icon">
          <ImgMain :src="displayImageURL"
                   :alt="name"
          />
        </div>
        <p class="grow">
          {{ name }}
        </p>
      </div>
    </TransitionGroup>
    <div v-if="foundReverted"
         class="display-flex flex-row gap-8"
    >
      <Button :color="'bg.mojito'"
              :size="'size.medium'"
              class="grow"
              :is-loading="isLoading"
      >
        <button class="compact-500-accent"
                type="button"
                @click="buyNFT(foundReverted.tokenList)"
        >
          Repeat reverted
        </button>
      </Button>
      <Button :size="'size.medium'">
        <button type="button"
                aria-label="next"
                @click="toNext"
        >
          <IconMain class="display-flex align-items-center compact-500-accent gap-8"
                    icon="arrow-r-l"
                    :size="24"
          />
        </button>
      </Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.transaction {
  &-row {
    &__icon {
      width: 36px;
      height: 36px;
    }
  }

  &__status {
    &--success {
      color: var(--content-brand-mojito);
    }

    &--reverted {
      color: var(--content-negative);
    }
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  transform: translateX(30px);
  opacity: 0;
}

</style>
